import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { portalApi, sessionHeaders, portalErrorMessage } from './portalApi';
import { carrierChoices, servicesForCarrier, carrierForService } from '../shippingCarriage';
import { thumbFileUrl } from '../mediaFileUrl';
import { COUNTRIES } from '../countries';
import { missingPart } from '../addressFormat';
import PublicAddressFields, { EMPTY_PUBLIC_ADDRESS, changeCountry } from '../PublicAddressFields';
import { askConfirm } from '../AppDialogs';

import CancelButton from '../CancelButton';
// Ordering in the portal (issue #284, vis_self_service_ordering.md): the
// Order page (/portal/<name>/order) to find products on the Customer's
// pricelists by name or SKU (a code typed in full narrows to that product)
// and add them, and the Cart
// (/portal/<name>/cart) to change quantities, choose delivery and shipping,
// and submit the order to staff. Each login has its own cart, kept until
// it's submitted or emptied. Prices arrive already worked out and formatted
// (backend/services/portalCartService.js); shipping is always to be
// confirmed by staff.

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };
const INPUT_STYLE = { height: 36, padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' };
const SECONDARY_BUTTON = { padding: '7px 12px', border: '1px solid #bfc8d6', color: '#1f2d3d', background: '#fff', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const DANGER_BUTTON = { ...SECONDARY_BUTTON, color: '#b91c1c', borderColor: '#fecaca' };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const LABEL_STYLE = { display: 'block', fontSize: 12, color: '#5a6b85', marginBottom: 4 };
const MUTED = { fontSize: 14, color: '#5a6b85' };
const TH = { padding: '8px 10px 8px 0', fontSize: 12, fontWeight: 600, color: '#5a6b85', textAlign: 'left', whiteSpace: 'nowrap' };
const TD = { padding: '10px 10px 10px 0', fontSize: 14, verticalAlign: 'top' };
const NUM = { textAlign: 'right' };
const SUB = { fontSize: 11, color: '#888' };
const QTY_INPUT = { ...INPUT_STYLE, width: 72, textAlign: 'right' };
const SEARCH_DELAY_MS = 300;

const SECTION_TITLE = { margin: '0 0 4px', fontSize: 15, fontWeight: 600 };
// A part of the checkout: Delivery address, Shipping, Your order. Each after
// the first has a rule above it.
const CHECKOUT_HEADING = { margin: 0, fontSize: 14, fontWeight: 600, color: '#1f2933' };
const CHECKOUT_PART = { borderTop: '1px solid #e3e8ef', paddingTop: 16 };
// Above this many delivery addresses (a chain's stores, say), the list gets
// a search box and scrolls.
const ADDRESS_SEARCH_FROM = 9;
// "= 20 PCS" under a Pack or Box quantity.
function BaseQty({ qty, perUnit, code }) {
  const n = Number(qty);
  if (!perUnit || !Number.isFinite(n) || n < 1) return null;
  return <div style={{ ...SUB, textAlign: 'right', marginTop: 2 }}>= {Math.round(n * perUnit * 10000) / 10000} {code}</div>;
}

const disabledLook = (off) => (off ? { cursor: 'not-allowed', opacity: 0.6 } : {});
const UPSELL = { background: '#e8f4fd', border: '1px solid #b6dcf5', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: '#0d5c8f' };

function Notice({ tone = 'info', children }) {
  const tones = {
    info: { background: '#eef5fd', border: '1px solid #c5dcf5', color: '#1f2d3d' },
    warn: { background: '#fff8e6', border: '1px solid #f0d9a6', color: '#8a5a00' },
    error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' },
    ok: { background: '#e6f4ea', border: '1px solid #b7dfc2', color: '#2e7d32' },
  };
  return <div style={{ ...tones[tone], borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{children}</div>;
}

// A requested delivery address still being checked: a Checking pill and
// the date it was asked for.
function PendingAddressStatus({ requestedDate }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#5a6b85', whiteSpace: 'nowrap' }}>
      <span style={{ padding: '2px 10px', borderRadius: 12, fontWeight: 600, background: '#fff4e0', color: '#8a5a00' }}>Pending</span>
      {requestedDate && <span>Requested {requestedDate}</span>}
    </span>
  );
}

// Signed-in requests for these pages; ends the session on a 401.
export function usePortalRequest(portalName, onSessionEnded) {
  return useCallback(async (method, path, body, params) => {
    try {
      return await portalApi.request({
        method,
        url: `/api/portal-auth/portals/${encodeURIComponent(portalName)}${path}`,
        data: body,
        params,
        headers: sessionHeaders(portalName),
      });
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionEnded();
        return null;
      }
      throw err;
    }
  }, [portalName, onSessionEnded]);
}

// Shown in place of ordering when the Customer can't order from the portal
// (a Login Switcher view of a Customer with no login).
function NotOrdering({ ordering }) {
  return (
    <div style={CARD_STYLE}>
      <h1 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600 }}>New order</h1>
      <div style={MUTED}>{ordering.message}</div>
    </div>
  );
}

// One unit of a product in the browse list: its price, a quantity and Add.
// The price follows the quantity typed, so a quantity break shows before
// the item is added.
function UnitRow({ product, unit: listed, request, onAdded, priceSuffix }) {
  const [qty, setQty] = useState('1');
  const [priced, setPriced] = useState(null); // { qty, ...price fields }
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { ok, text }

  useEffect(() => {
    const n = Number(qty);
    if (!listed.unit_price || !Number.isInteger(n) || n <= 1) { setPriced(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      request('get', '/catalogue/price', null, { product_id: product.product_id, uom_id: listed.uom_id, qty: n })
        .then((res) => { if (res && !cancelled) setPriced(res.data.data); })
        .catch(() => { if (!cancelled) setPriced(null); });
    }, SEARCH_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [qty, request, product.product_id, listed.uom_id, listed.unit_price]);

  const unit = priced && String(priced.qty) === qty ? { ...listed, ...priced } : listed;

  const add = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await request('post', '/cart/lines', { product_id: product.product_id, uom_id: unit.uom_id, qty });
      if (res) {
        setFeedback({ ok: true, text: 'Added' });
        setQty('1');
        onAdded();
      }
    } catch (err) {
      setFeedback({ ok: false, text: portalErrorMessage(err, 'Could not add') });
    }
    setBusy(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <span style={{ fontSize: 13, minWidth: 70 }}>{unit.name || unit.code}</span>
      <span style={{ fontSize: 14, minWidth: 90, textAlign: 'right' }}>
        {unit.unit_price || 'Price on request'}
        {unit.unit_price && priceSuffix && <div style={SUB}>{priceSuffix.trim().replace(/[()]/g, '')}</div>}
        {unit.base_price && <div style={SUB}>{unit.base_price}</div>}
      </span>
      <div>
        <input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} aria-label={`Quantity of ${product.sku} in ${unit.name || unit.code}`} style={QTY_INPUT} />
        <BaseQty qty={qty} perUnit={unit.base_per_unit} code={unit.base_code} />
      </div>
      <button type="button" onClick={add} disabled={busy || !unit.unit_price} title={`Add ${product.sku} in ${unit.name || unit.code} to your cart`} style={{ ...SECONDARY_BUTTON, ...disabledLook(busy || !unit.unit_price) }}>
        {busy ? 'Adding...' : 'Add'}
      </button>
      {unit.next_break && (
        <div style={{ flexBasis: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <span style={UPSELL}>
            Buy {unit.next_break.qty} {unit.next_break.unit} or more for {unit.next_break.price} {unit.next_break.per}
          </span>
        </div>
      )}
      {feedback && <span style={{ fontSize: 12, color: feedback.ok ? '#2e7d32' : '#b91c1c', flexBasis: '100%', textAlign: 'right' }}>{feedback.text}</span>}
    </div>
  );
}

// A product's primary photo, or a plain tile when it has none.
function ProductPhoto({ product, size }) {
  const [failed, setFailed] = useState(false);
  const box = { width: size, height: size, flex: '0 0 auto', borderRadius: 6, background: '#f3f5f8', border: '1px solid #eef1f5', boxSizing: 'border-box' };
  if (!product.image_id || failed) return <div style={box} aria-hidden="true" />;
  return <img src={thumbFileUrl(product.image_id)} alt={product.name || product.sku} onError={() => setFailed(true)} style={{ ...box, objectFit: 'contain', background: '#fff' }} />;
}

// One Top seller as a card: photo, SKU, name, the price of the chosen unit
// and Add for one of it.
function TopSellerCard({ product, request, onAdded, priceSuffix }) {
  const [uomId, setUomId] = useState(product.units[0]?.uom_id);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { ok, text }
  const unit = product.units.find((u) => u.uom_id === uomId) || product.units[0];
  const unitName = unit ? unit.name || unit.code : '';

  const add = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await request('post', '/cart/lines', { product_id: product.product_id, uom_id: unit.uom_id, qty: '1' });
      if (res) {
        setFeedback({ ok: true, text: 'Added' });
        onAdded();
      }
    } catch (err) {
      setFeedback({ ok: false, text: portalErrorMessage(err, 'Could not add') });
    }
    setBusy(false);
  };

  const off = busy || !unit?.unit_price;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}><ProductPhoto product={product} size={120} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{product.sku}</div>
        <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{product.name}</div>
      </div>
      <div style={{ marginTop: 'auto' }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {unit?.unit_price || 'Price on request'}
          {unit?.unit_price && priceSuffix && <span style={{ ...SUB, fontWeight: 400, marginLeft: 4 }}>{priceSuffix.trim().replace(/[()]/g, '')}</span>}
        </div>
        {product.units.length > 1 ? (
          <select value={uomId} onChange={(e) => setUomId(Number(e.target.value))} aria-label={`Unit of ${product.sku}`} style={{ ...INPUT_STYLE, height: 30, padding: '4px 6px', fontSize: 13, width: '100%', marginTop: 6 }}>
            {product.units.map((u) => <option key={u.uom_id} value={u.uom_id}>{u.name || u.code}</option>)}
          </select>
        ) : (
          <div style={{ ...SUB, marginTop: 2 }}>per {unitName}</div>
        )}
        <button type="button" onClick={add} disabled={off} title={`Add one ${unitName} of ${product.sku} to your cart`} style={{ ...SECONDARY_BUTTON, width: '100%', marginTop: 8, ...disabledLook(off) }}>
          {busy ? 'Adding...' : 'Add'}
        </button>
        {feedback && <div style={{ fontSize: 12, marginTop: 4, color: feedback.ok ? '#2e7d32' : '#b91c1c' }}>{feedback.text}</div>}
      </div>
    </div>
  );
}

const TOP_SELLER_CARD_WIDTH = 168;
const TOP_SELLER_GAP = 12;
const ARROW_BUTTON = { ...SECONDARY_BUTTON, padding: '4px 10px', fontSize: 15, lineHeight: 1 };

// The Top sellers as one row of cards that scrolls sideways at any width:
// swiped on a phone, or moved a few cards at a time with the arrows, which
// show only while the row is wider than the page.
function TopSellers({ products, scope, request, onAdded, priceSuffix }) {
  const rowRef = useRef(null);
  const [ends, setEnds] = useState({ start: true, end: true });

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    setEnds({ start: row.scrollLeft <= 1, end: row.scrollLeft + row.clientWidth >= row.scrollWidth - 1 });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, products]);

  const scroll = (direction) => {
    const row = rowRef.current;
    const step = TOP_SELLER_CARD_WIDTH + TOP_SELLER_GAP;
    row.scrollBy({ left: direction * Math.max(Math.floor(row.clientWidth / step), 1) * step, behavior: 'smooth' });
  };

  const scrolls = !(ends.start && ends.end);
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div>
          <h2 style={SECTION_TITLE}>Recommended for you</h2>
          <div style={{ fontSize: 12, color: '#5a6b85' }}>
            {scope === 'all' ? 'Our best sellers over the last 90 days.' : 'What you’ve bought most over the last 90 days.'}
          </div>
        </div>
        {scrolls && (
          <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
            <button type="button" onClick={() => scroll(-1)} disabled={ends.start} aria-label="Previous recommendations" title="Show the previous recommendations" style={{ ...ARROW_BUTTON, ...disabledLook(ends.start) }}>‹</button>
            <button type="button" onClick={() => scroll(1)} disabled={ends.end} aria-label="More recommendations" title="Show more recommendations" style={{ ...ARROW_BUTTON, ...disabledLook(ends.end) }}>›</button>
          </div>
        )}
      </div>
      <div
        ref={rowRef}
        onScroll={measure}
        style={{ display: 'flex', gap: TOP_SELLER_GAP, overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}
      >
        {products.map((p) => (
          <div key={p.product_id} style={{ flex: `0 0 ${TOP_SELLER_CARD_WIDTH}px`, scrollSnapAlign: 'start', display: 'flex' }}>
            <TopSellerCard product={p} request={request} onAdded={onAdded} priceSuffix={priceSuffix} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductList({ products, request, onAdded, priceSuffix }) {
  return products.map((p, i) => (
    <div key={p.product_id} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'space-between', padding: '12px 0', borderTop: i === 0 ? 'none' : '1px solid #eef1f5' }}>
      <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <ProductPhoto product={p} size={56} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {p.sku}
            {p.availability_badge && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: '#eef1f5', color: '#3d4b63' }}>{p.availability_badge}</span>}
          </div>
          <div style={{ fontSize: 14 }}>{p.name}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: '0 1 auto' }}>
        {p.units.map((u) => <UnitRow key={u.uom_id} product={p} unit={u} request={request} onAdded={onAdded} priceSuffix={priceSuffix} />)}
      </div>
    </div>
  ));
}

// Search the products on the Customer's pricelists by name or SKU. With
// nothing typed, the Top sellers and the Customer's Frequently ordered
// products show, and Browse all products lists every product a page at a
// time. A code that isn't on them gets a message saying it isn't available.
function Browse({ request, onAdded }) {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setData(null);
    request('get', '/catalogue', null, { search: search || undefined, browse: !search && browsing ? 'true' : undefined, page })
      .then((res) => { if (res && !cancelled) setData(res.data.data); })
      .catch((err) => { if (!cancelled) setError(portalErrorMessage(err, 'Could not load products')); });
    return () => { cancelled = true; };
  }, [request, search, browsing, page]);

  const listing = Boolean(search) || browsing;
  const frequent = data?.frequent || [];
  const topSellers = data?.top_sellers || [];
  const browse = (on) => { setBrowsing(on); setPage(1); };

  return (
    <div>
      <input
        type="search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search name or SKU..."
        aria-label="Search name or SKU"
        autoFocus
        style={{ ...INPUT_STYLE, width: '100%', maxWidth: 440, marginBottom: 14 }}
      />
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && !data && <div style={MUTED}>Loading...</div>}
      {data && data.total === 0 && !search && <div style={MUTED}>No products are available to you at the moment.</div>}
      {data && search && data.products.length === 0 && (
        <div style={MUTED}>
          Nothing matches &ldquo;{search}&rdquo;. If that&apos;s a product code, it isn&apos;t available to you. Contact us if you need it.
        </div>
      )}
      {data && !listing && (
        <>
          {topSellers.length > 0 && (
            <TopSellers products={topSellers} scope={data.top_sellers_scope} request={request} onAdded={onAdded} priceSuffix={data.price_suffix} />
          )}
          {frequent.length > 0 && (
            <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', borderRadius: 8, padding: '14px 16px 4px', marginBottom: 24 }}>
              <h2 style={SECTION_TITLE}>Frequently ordered</h2>
              <div style={{ fontSize: 12, color: '#5a6b85', marginBottom: 4 }}>What you&apos;ve ordered most in the last 12 months.</div>
              <ProductList products={frequent} request={request} onAdded={onAdded} priceSuffix={data.price_suffix} />
            </div>
          )}
          {data.total > 0 && (
            <button type="button" onClick={() => browse(true)} title="List every product you can order" style={SECONDARY_BUTTON}>
              Browse all products ({data.total})
            </button>
          )}
        </>
      )}
      {data && listing && data.products.length > 0 && (
        <>
          {!search && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, paddingBottom: 8, borderBottom: '1px solid #e3e7ee' }}>
              <h2 style={{ ...SECTION_TITLE, margin: 0 }}>All products</h2>
              <button type="button" onClick={() => browse(false)} title="Show your recommended and frequently ordered products" style={LINK_BUTTON}>‹ Back to recommended</button>
            </div>
          )}
          <ProductList products={data.products} request={request} onAdded={onAdded} priceSuffix={data.price_suffix} />
          {data.pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', marginTop: 12, fontSize: 13 }}>
              <button type="button" onClick={() => setPage(data.page - 1)} disabled={data.page <= 1} title="Show the previous page of products" style={{ ...SECONDARY_BUTTON, ...disabledLook(data.page <= 1) }}>‹ Previous</button>
              <span>Page {data.page} of {data.pages}</span>
              <button type="button" onClick={() => setPage(data.page + 1)} disabled={data.page >= data.pages} title="Show the next page of products" style={{ ...SECONDARY_BUTTON, ...disabledLook(data.page >= data.pages) }}>Next ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Goes to the Cart to check out; off while the cart is empty.
function ProceedToCart({ count, onNavigate }) {
  const off = !count;
  return (
    <button type="button" onClick={() => onNavigate('cart')} disabled={off} title="Show your cart to check out" style={{ ...PRIMARY_BUTTON, ...disabledLook(off) }}>
      Proceed to cart{count ? ` (${count})` : ''}
    </button>
  );
}

export function PortalOrderPage({ portalName, ordering, cartCount, onCartChanged, onNavigate, onSessionEnded }) {
  const request = usePortalRequest(portalName, onSessionEnded);
  if (!ordering.can_order) return <NotOrdering ordering={ordering} />;
  return (
    <div style={CARD_STYLE}>
      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => onNavigate('orders')} title="Show all your orders" style={LINK_BUTTON}>‹ All orders</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>New order</h1>
          <div style={{ ...MUTED, fontSize: 13, marginTop: 4 }}>Find products and add them to your cart, then proceed to your cart to check out.</div>
        </div>
        <ProceedToCart count={cartCount} onNavigate={onNavigate} />
      </div>
      <Browse request={request} onAdded={onCartChanged} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <ProceedToCart count={cartCount} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

// ---- Cart ------------------------------------------------------------------

function TotalRow({ label, value, strong }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 24,
      fontSize: strong ? 17 : 14, fontWeight: strong ? 700 : 400, padding: strong ? '8px 0' : '4px 0',
      ...(strong ? { borderTop: '1px solid #b3bbc7', marginTop: 4 } : {}),
    }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// A line's quantity, saved when the field is left or Enter is pressed.
function QtyField({ line, onSave, disabled }) {
  const [value, setValue] = useState(String(line.qty));
  useEffect(() => { setValue(String(line.qty)); }, [line.qty]);
  const commit = () => {
    if (value === String(line.qty)) return;
    onSave(line, value, () => setValue(String(line.qty)));
  };
  return (
    <div style={{ display: 'inline-block' }}>
      <input
        type="number"
        min="1"
        step="1"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        aria-label={`Quantity of ${line.product_code}`}
        style={QTY_INPUT}
      />
      <BaseQty qty={value} perUnit={line.base_per_unit} code={line.base_code} />
    </div>
  );
}

function CartLines({ cart, busy, onQty, onRemove }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
            <th style={TH}>Product code</th>
            <th style={TH}>Description</th>
            <th style={TH}>Unit</th>
            <th style={{ ...TH, ...NUM }}>Qty</th>
            <th style={{ ...TH, ...NUM }}>Unit price{cart.price_suffix}</th>
            <th style={{ ...TH, ...NUM }}>Line total{cart.price_suffix}</th>
            <th style={TH} aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          {cart.lines.map((line) => (
            <tr key={line.id} style={{ borderBottom: '1px solid #eef1f5' }}>
              <td style={{ ...TD, whiteSpace: 'nowrap' }}>{line.product_code}</td>
              <td style={TD}>
                {line.description}
                {!line.on_pricelist && <div style={{ fontSize: 12, color: '#8a5a00' }}>Not on your pricelists. We&apos;ll confirm this item.</div>}
                {line.next_break && (
                  <div style={{ ...UPSELL, display: 'inline-block', marginTop: 4 }}>
                    Order {line.next_break.qty} {line.next_break.unit} for {line.next_break.price} {line.next_break.per} ({line.next_break.total} total)
                  </div>
                )}
              </td>
              <td style={TD}>{line.unit}</td>
              <td style={{ ...TD, ...NUM }}><QtyField line={line} onSave={onQty} disabled={busy} /></td>
              <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                {line.unit_price || '—'}
                {line.base_price && <div style={SUB}>{line.base_price}</div>}
              </td>
              <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                {line.line_total}
              </td>
              <td style={{ ...TD, textAlign: 'right' }}>
                <button type="button" onClick={() => onRemove(line)} disabled={busy} title={`Remove ${line.product_code} from your cart`} style={{ ...LINK_BUTTON, color: '#b91c1c', ...disabledLook(busy) }}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const idString = (id) => (id == null ? '' : String(id));

const ADDRESS_INPUT_STYLE = { ...INPUT_STYLE, width: '100%', maxWidth: 360 };

// One labelled address part, an amber note under it for a part that needs
// a second look (the Soft Warning pattern in ui-architecture.md).
function RequestField({ id, label, warning, children }) {
  return (
    <div>
      <label style={LABEL_STYLE} htmlFor={id}>{label}</label>
      {children}
      {warning && (
        <div style={{ fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '4px 8px', marginTop: 4, maxWidth: 360 }}>{warning}</div>
      )}
    </div>
  );
}

// Asking for a new delivery address at checkout (issue #339). Sending it
// doesn't add an address: staff verify it, add it, and the Customer is
// emailed once it can be chosen here. A <div>, not a <form>, since it sits
// inside the checkout form (ui-architecture.md, Never nest a form). The
// address follows its country's layout (#395), starting on the country of
// the Customer's Primary Address.
function AddressRequestForm({ request, onSent, onCancel }) {
  const [phoneOnRecord, setPhoneOnRecord] = useState(null);
  const [address, setAddress] = useState(null);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    request('get', '/address-requests/options')
      .then((res) => {
        if (!res || cancelled) return;
        setPhoneOnRecord(res.data.data.phone_on_record);
        setAddress({ ...EMPTY_PUBLIC_ADDRESS, country: res.data.data.country || 'Australia' });
      })
      .catch((err) => { if (!cancelled) setError(portalErrorMessage(err, 'Could not load the request form')); });
    return () => { cancelled = true; };
  }, [request]);

  // Australia Post's Suburbs for an Australian Postcode, as it's typed
  // (#158).
  const fetchPostcodeCheck = async (query) => {
    const res = await request('get', '/postcode-check', undefined, query);
    return res?.data.data;
  };
  const send = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await request('post', '/address-requests', { ...address, phone: phoneOnRecord ? '' : phone });
      if (res) {
        onSent();
        return;
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not send your request'));
    }
    setSaving(false);
  };

  // Enter mustn't submit the order form around this one.
  const noSubmit = (e) => { if (e.key === 'Enter') e.preventDefault(); };
  const ready = !saving && phoneOnRecord !== null && address && !missingPart(address) && (phoneOnRecord || phone.trim());

  return (
    <div style={{ border: '1px solid #e3e8ef', borderRadius: 8, padding: 16, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }} onKeyDown={noSubmit}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>Request a new delivery address</div>
      <div style={{ ...MUTED, fontSize: 13 }}>To protect your deliveries, we need to confirm new addresses. We&apos;ll email you when you can use the new address. Your cart is kept in the meantime.</div>
      {address && (
        <>
          <RequestField id="portal-request-country" label="Country">
            <select id="portal-request-country" value={address.country} onChange={(e) => setAddress(changeCountry(address, e.target.value))} style={ADDRESS_INPUT_STYLE}>
              {!COUNTRIES.includes(address.country) && <option value={address.country}>{address.country}</option>}
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </RequestField>
          <PublicAddressFields
            idPrefix="portal-request"
            address={address}
            onChange={setAddress}
            Field={RequestField}
            inputStyle={ADDRESS_INPUT_STYLE}
            fetchCheck={fetchPostcodeCheck}
          />
        </>
      )}
      {phoneOnRecord === false && (
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-request-phone">Phone number</label>
          <input id="portal-request-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} style={{ ...INPUT_STYLE, width: 220 }} />
          <div style={{ ...SUB, marginTop: 4 }}>We may call this number to confirm the address.</div>
        </div>
      )}
      {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
      <div>
        <button type="button" onClick={send} disabled={!ready} title="Send this address to us to add" style={{ ...PRIMARY_BUTTON, ...disabledLook(!ready) }}>
          {saving ? 'Sending...' : 'Send request'}
        </button>
        <CancelButton onClick={onCancel} disabled={saving} title="Close this form" style={{ marginLeft: 8 }} />
      </div>
    </div>
  );
}

// Delivery address, shipping (Carrier, Service, Options — the same order as
// My account's Preferences), PO reference and a note, then Submit.
function Checkout({ portalName, cart, request, onSubmitted, disabled }) {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [addressId, setAddressId] = useState(idString(cart.checkout.address_id));
  const [addressSearch, setAddressSearch] = useState('');
  const [carrierId, setCarrierId] = useState(idString(cart.checkout.carrier_id));
  const [serviceId, setServiceId] = useState(idString(cart.checkout.shipping_service_id));
  const [optionIds, setOptionIds] = useState(cart.checkout.option_ids.map(String));
  const [poReference, setPoReference] = useState(cart.checkout.po_reference);
  const [note, setNote] = useState(cart.checkout.note);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  // Bumped after a request is sent, to reload the pending requests.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    request('get', '/cart/checkout-options')
      .then((res) => {
        if (!res || cancelled) return;
        const data = res.data.data;
        setOptions(data);
        // An address no longer offered for delivery falls back to the first one.
        setAddressId((current) => (data.addresses.some((a) => String(a.id) === current) ? current : idString(data.addresses[0]?.id)));
      })
      .catch((err) => { if (!cancelled) setLoadError(portalErrorMessage(err, 'Could not load delivery and shipping choices')); });
    return () => { cancelled = true; };
  }, [request, reloadKey]);

  if (loadError) return <div style={{ color: 'red', fontSize: 13 }}>{loadError}</div>;
  if (!options) return <div style={MUTED}>Loading...</div>;

  const service = options.services.find((s) => String(s.id) === serviceId);
  const pickCarrier = (value) => { setCarrierId(value); setServiceId(''); setOptionIds([]); };
  const pickService = (value) => { setCarrierId(carrierForService(options.services, value, carrierId)); setServiceId(value); setOptionIds([]); };
  const toggleOption = (id) => setOptionIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  const noAddress = options.addresses.length === 0;
  const pending = options.pending_addresses || [];
  const manyAddresses = options.addresses.length >= ADDRESS_SEARCH_FROM;
  const addressTerm = addressSearch.trim().toLowerCase();
  // The chosen address stays in the list whatever the search.
  const shownAddresses = manyAddresses && addressTerm
    ? options.addresses.filter((a) => String(a.id) === addressId || [...a.lines, a.type || ''].join(' ').toLowerCase().includes(addressTerm))
    : options.addresses;
  const ready = !disabled && !noAddress && addressId && serviceId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await request('post', '/cart/submit', {
        address_id: Number(addressId),
        shipping_service_id: Number(serviceId),
        option_ids: optionIds.map(Number),
        po_reference: poReference,
        note,
      });
      if (res) {
        onSubmitted(res.data.data);
        return;
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not submit your order'));
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h3 style={{ ...CHECKOUT_HEADING, marginBottom: 8 }}>Delivery address</h3>
        {noAddress ? (
          pending.length > 0 ? (
            pending.map((p) => (
              <Notice key={p.id} tone="info">
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{p.lines.join(', ')}</span>
                  <PendingAddressStatus requestedDate={p.requested_date} />
                </div>
                <div style={{ marginTop: 6 }}>We&apos;re checking this address and will email you once you can choose it here. Your cart is kept.</div>
              </Notice>
            ))
          ) : (
            <Notice tone="warn">There&apos;s no delivery address on file for you. Request one below, then submit your order once we&apos;ve added it. Your cart is kept.</Notice>
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {manyAddresses && (
              <input
                type="search"
                value={addressSearch}
                onChange={(e) => setAddressSearch(e.target.value)}
                placeholder={`Search ${options.addresses.length} addresses by street, suburb or type`}
                aria-label="Search delivery addresses"
                style={{ ...INPUT_STYLE, width: '100%', maxWidth: 420 }}
              />
            )}
            <div style={manyAddresses ? { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 } : { display: 'contents' }}>
            {shownAddresses.length === 0 && <div style={{ ...MUTED, fontSize: 13 }}>No address matches “{addressSearch.trim()}”.</div>}
            {shownAddresses.map((a) => (
              <label key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}>
                <input type="radio" name="portal-delivery-address" checked={addressId === String(a.id)} onChange={() => setAddressId(String(a.id))} style={{ marginTop: 3 }} />
                <span>
                  {a.lines.join(', ')}
                  {a.type && <span style={{ fontSize: 12, color: '#5a6b85' }}> · {a.type}</span>}
                </span>
              </label>
            ))}
            </div>
            {pending.map((p) => (
              <label key={`pending-${p.id}`} title="We're checking this address. We'll email you once you can choose it." style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: '#8a94a6' }}>
                <input type="radio" disabled style={{ marginTop: 3 }} />
                <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  {p.lines.join(', ')}
                  <PendingAddressStatus requestedDate={p.requested_date} />
                </span>
              </label>
            ))}
          </div>
        )}
        {requesting ? (
          <AddressRequestForm request={request} onSent={() => { setRequesting(false); setReloadKey((k) => k + 1); }} onCancel={() => setRequesting(false)} />
        ) : (
          <button type="button" onClick={() => setRequesting(true)} title="Open a form to request a new delivery address" style={{ ...LINK_BUTTON, marginTop: noAddress ? 0 : 10 }}>
            Request a new delivery address
          </button>
        )}
      </div>
      {/* Hidden while a delivery address request is open and unsent, so the
          order can't go to an old address by mistake. */}
      {!noAddress && !requesting && (
        <>
          <h3 style={{ ...CHECKOUT_HEADING, ...CHECKOUT_PART }}>Shipping</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={LABEL_STYLE} htmlFor="portal-checkout-carrier">Carrier</label>
              <select id="portal-checkout-carrier" value={carrierId} onChange={(e) => pickCarrier(e.target.value)} style={{ ...INPUT_STYLE, width: 240 }}>
                <option value="">Choose...</option>
                {carrierChoices(options.services, carrierId).map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={LABEL_STYLE} htmlFor="portal-checkout-service">Shipping Service</label>
              <select id="portal-checkout-service" value={serviceId} onChange={(e) => pickService(e.target.value)} style={{ ...INPUT_STYLE, width: 240 }}>
                <option value="">Choose...</option>
                {servicesForCarrier(options.services, carrierId, serviceId).map((s) => <option key={s.id} value={String(s.id)}>{s.description}</option>)}
              </select>
            </div>
          </div>
          {service && service.options.length > 0 && (
            <div>
              <span style={LABEL_STYLE}>Shipping Options</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {service.options.map((o) => (
                  <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={optionIds.includes(String(o.id))} onChange={() => toggleOption(String(o.id))} />
                    {o.description}
                  </label>
                ))}
              </div>
            </div>
          )}
          <h3 style={{ ...CHECKOUT_HEADING, ...CHECKOUT_PART }}>Your order</h3>
          <div>
            <label style={LABEL_STYLE} htmlFor="portal-checkout-po">PO reference (optional)</label>
            <input id="portal-checkout-po" value={poReference} onChange={(e) => setPoReference(e.target.value)} maxLength={255} style={{ ...INPUT_STYLE, width: '100%', maxWidth: 360 }} />
          </div>
          <div>
            <label style={LABEL_STYLE} htmlFor="portal-checkout-note">Note to us (optional)</label>
            <textarea id="portal-checkout-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} style={{ ...INPUT_STYLE, height: 'auto', width: '100%', maxWidth: 560, resize: 'vertical' }} />
          </div>
          {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
          <div>
            <button type="submit" disabled={saving || !ready} title="Place this order with us" style={{ ...PRIMARY_BUTTON, ...disabledLook(saving || !ready) }}>
              {saving ? 'Submitting...' : cart.editing ? 'Resubmit order' : 'Submit order'}
            </button>
            <div style={{ ...MUTED, fontSize: 12, marginTop: 6 }}>
              Submitting places this order at these prices under our <a href={`/portal/${encodeURIComponent(portalName)}/terms`} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions of Trade</a>.
              Freight is an estimate until we confirm it. You can change or withdraw the order until we accept it.
            </div>
          </div>
        </>
      )}
    </form>
  );
}

export function PortalCartPage({ portalName, ordering, onCartChanged, onNavigate, onSessionEnded }) {
  const request = usePortalRequest(portalName, onSessionEnded);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);
  // The cart's items, and Delivery and checkout, open unless folded away
  // (#158).
  const [linesOpen, setLinesOpen] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await request('get', '/cart');
      if (res) {
        setData(res.data.data);
        onCartChanged(res.data.data.cart ? res.data.data.cart.lines.length : 0);
      }
    } catch (err) {
      setLoadError(portalErrorMessage(err, 'Could not load your cart'));
    }
  }, [request, onCartChanged]);

  useEffect(() => { load(); }, [load]);

  // Runs one change to the cart, then reloads it.
  const change = async (action, fallback) => {
    setBusy(true);
    setError('');
    try {
      await action();
      await load();
    } catch (err) {
      setError(portalErrorMessage(err, fallback));
    }
    setBusy(false);
  };

  const setQty = (line, value, revert) => change(async () => {
    try {
      await request('put', `/cart/lines/${line.id}`, { qty: value });
    } catch (err) {
      revert();
      throw err;
    }
  }, 'Could not change the quantity');
  const removeLine = (line) => change(() => request('delete', `/cart/lines/${line.id}`), 'Could not remove that item');
  const emptyCart = async () => {
    if (!(await askConfirm('Remove everything from your cart?', { confirmLabel: 'Empty Cart', destructive: true }))) return;
    change(() => request('delete', '/cart'), 'Could not empty your cart');
  };
  const withdraw = async (cart) => {
    if (!(await askConfirm(`Withdraw order ${cart.editing}? It will be removed and won't be sent to us.`, { confirmLabel: 'Withdraw', destructive: true }))) return;
    change(() => request('post', `/orders/${cart.id}/withdraw`), 'Could not withdraw this order');
  };

  if (!ordering.can_order) return <NotOrdering ordering={ordering} />;

  if (submitted) {
    return (
      <div style={CARD_STYLE}>
        <h1 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600 }}>Order submitted</h1>
        <p style={{ fontSize: 15, margin: '0 0 6px' }}>Thank you. Your order number is <strong>{submitted.display_number}</strong>.</p>
        <p style={{ ...MUTED, margin: '0 0 18px' }}>We&apos;ll confirm freight and accept it. Until then you can change or withdraw it from the order page.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => onNavigate(`orders/${submitted.id}`)} title={`Show order ${submitted.display_number}`} style={PRIMARY_BUTTON}>View order</button>
          <button type="button" onClick={() => onNavigate('order')} title="Find products for a new order" style={SECONDARY_BUTTON}>Start a new order</button>
        </div>
      </div>
    );
  }

  if (loadError) return <div style={CARD_STYLE}><div style={{ color: 'red', fontSize: 13 }}>{loadError}</div></div>;
  if (!data) return <div style={CARD_STYLE}><div style={MUTED}>Loading...</div></div>;

  const { cart } = data;
  const empty = !cart || cart.lines.length === 0;

  return (
    <>
      <div style={CARD_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            {!empty && (
              <button
                type="button"
                onClick={() => setLinesOpen((o) => !o)}
                aria-expanded={linesOpen}
                title={linesOpen ? 'Hide the items in your cart' : 'Show the items in your cart'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#5a6b85', display: 'inline-flex', alignItems: 'center' }}
              >
                {/* Same turning chevron as the shared Accordion. */}
                <ChevronRight size={18} strokeWidth={2} style={{ color: '#9aa5b5', transform: linesOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            )}
            {cart?.editing ? `Editing order ${cart.editing}` : 'Cart'}
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => onNavigate('order')} title="Find more products to add" style={SECONDARY_BUTTON}>Add products</button>
            {cart && (cart.editing ? (
              <button type="button" onClick={() => withdraw(cart)} disabled={busy} title={`Remove order ${cart.editing}`} style={{ ...DANGER_BUTTON, ...disabledLook(busy) }}>Withdraw order</button>
            ) : !empty && (
              <button type="button" onClick={emptyCart} disabled={busy} title="Remove everything from your cart" style={{ ...DANGER_BUTTON, ...disabledLook(busy) }}>Empty cart</button>
            ))}
          </div>
        </div>
        {cart?.editing && <Notice>This order is back in your cart, so we won&apos;t act on it until you resubmit it.</Notice>}
        {cart?.price_error && <Notice tone="warn">{cart.price_error}</Notice>}
        {error && <Notice tone="error">{error}</Notice>}
        {empty ? (
          <div style={MUTED}>Your cart is empty.</div>
        ) : !linesOpen ? (
          // Folded away so a long cart doesn't push the checkout down.
          <button type="button" onClick={() => setLinesOpen(true)} title="Show the items in your cart" style={{ ...LINK_BUTTON, fontSize: 14 }}>
            {cart.lines.length} {cart.lines.length === 1 ? 'item' : 'items'} · {cart.totals.total}
          </button>
        ) : (
          <>
            <CartLines cart={cart} busy={busy} onQty={setQty} onRemove={removeLine} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <div style={{ width: '100%', maxWidth: 340 }}>
                {/* Business: Subtotal (ex GST), GST, Total. Retail: Total, then
                    the GST it includes. */}
                {cart.totals.subtotal && <TotalRow label={cart.totals.subtotal_label} value={cart.totals.subtotal} />}
                {cart.totals.discount && <TotalRow label={cart.totals.discount_label} value={cart.totals.discount} />}
                {cart.totals.gst && cart.pricing_basis !== 'retail' && <TotalRow label={cart.totals.gst_label} value={cart.totals.gst} />}
                <TotalRow label="Shipping" value="To be added" />
                <TotalRow label={`Total (excl. shipping${cart.price_suffix ? ', inc GST' : ''})`} value={cart.totals.total} strong />
                {cart.totals.gst && cart.pricing_basis === 'retail' && <TotalRow label={cart.totals.gst_label} value={cart.totals.gst} />}
              </div>
            </div>
          </>
        )}
      </div>
      {cart && (
        <div style={{ ...CARD_STYLE, marginTop: 16 }}>
          <h2 style={{ margin: checkoutOpen ? '0 0 14px' : 0, fontSize: 16, fontWeight: 600 }}>
            <button
              type="button"
              onClick={() => setCheckoutOpen((o) => !o)}
              aria-expanded={checkoutOpen}
              title={checkoutOpen ? 'Hide delivery and checkout' : 'Show delivery and checkout'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <ChevronRight size={18} strokeWidth={2} style={{ color: '#9aa5b5', transform: checkoutOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              Delivery and checkout
            </button>
          </h2>
          {/* Hidden, not removed, so a folded checkout keeps what's filled in. */}
          <div style={{ display: checkoutOpen ? 'block' : 'none' }}>
          <Checkout
            key={cart.id}
            portalName={portalName}
            cart={cart}
            request={request}
            disabled={empty || busy}
            onSubmitted={(order) => { setSubmitted(order); onCartChanged(0); }}
          />
          </div>
        </div>
      )}
    </>
  );
}
